import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon, ErrorOutline } from '@mui/icons-material';
import { useAuth, LOGIN_INVALID_CREDENTIALS_MESSAGE } from '../../contexts/AuthContext';

export default function LoginPage() {
  const { login, clearAuthError, authError } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (authError) setLoginError(authError);
  }, [authError]);

  const credentialsError =
    loginError === LOGIN_INVALID_CREDENTIALS_MESSAGE ||
    loginError?.toLowerCase().includes('incorrect username') ||
    loginError?.toLowerCase().includes('invalid username');

  const dismissError = () => {
    setLoginError(null);
    clearAuthError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    clearAuthError();

    const u = username.trim();
    const p = password.trim();
    if (!u || !p) {
      setLoginError('Username and password are required.');
      return;
    }

    setSubmitting(true);
    try {
      const ok = await login(u, p);
      if (!ok) {
        // AuthContext sets authError; useEffect above mirrors it into loginError.
      }
    } catch {
      setLoginError(LOGIN_INVALID_CREDENTIALS_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        '@supports (height: 100dvh)': { minHeight: '100dvh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 420,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/assets/main_logo.png"
            alt="Intellicare"
            sx={{ height: 48, width: 'auto', mb: 1 }}
          />
          <Typography variant="h5" fontWeight={600} color="#012676">
            Intellicare
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Sign in to continue
          </Typography>
        </Box>

        {loginError && (
          <Alert
            severity="error"
            icon={<ErrorOutline fontSize="inherit" />}
            onClose={dismissError}
            role="alert"
            aria-live="assertive"
            sx={{
              mb: 2,
              borderRadius: 1.5,
              alignItems: 'center',
              bgcolor: '#fdecea',
              color: '#b71c1c',
              border: '1px solid #ef9a9a',
              '& .MuiAlert-icon': { color: '#c62828' },
              '& .MuiAlert-message': { width: '100%', fontWeight: 600 },
            }}
          >
            {loginError}
          </Alert>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (loginError) dismissError();
            }}
            autoComplete="username"
            margin="normal"
            autoFocus
            error={Boolean(credentialsError)}
            sx={{
              '& .MuiOutlinedInput-root.Mui-error fieldset': {
                borderColor: '#c62828',
                borderWidth: 2,
              },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (loginError) dismissError();
            }}
            autoComplete="current-password"
            margin="normal"
            error={Boolean(credentialsError)}
            sx={{
              '& .MuiOutlinedInput-root.Mui-error fieldset': {
                borderColor: '#c62828',
                borderWidth: 2,
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            disabled={submitting}
            sx={{
              mt: 3,
              mb: 1,
              py: 1.5,
              bgcolor: '#012676',
              '&:hover': { bgcolor: '#011a5c' },
            }}
          >
            {submitting ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
